import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, App, Button, Tag, type MenuProps, type TableProps } from 'antd'
import { ChevronDown, ListChecks, Plus } from 'lucide-react'
import { z } from 'zod'
import type { AccountDisplay } from '@super-mega/server/contracts/profile'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { CREATED_AT_SORT_DIRECTIONS, DEFAULT_PAGE_SEARCH as DEFAULT_QUERY } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { AuthorizedDropdown } from '@/features/authorization/authorized-dropdown.tsx'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { CustomerTagEditorModal, type CustomerTagEditorRecord } from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import {
  useMemberCreatorOptions,
  usePageTableNavigation,
  type DateRangeFormValue,
  type PageTableSearch
} from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import {
  createdAtSearchShape,
  createdAtSortSearchShape,
  creatorMemberSearchShape,
  getCreatedAtSearch,
  getCreatedAtSortOrder,
  getDateRangeFormValue
} from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

type CustomerTagQuery = PageTableSearch & {
  keyword?: string
  creatorMemberId?: number
  createdFrom?: string
  createdTo?: string
  sortField?: 'createdAt'
  sortOrder?: 'asc' | 'desc'
}

type CustomerTagSearchFormValues = Pick<CustomerTagQuery, 'creatorMemberId' | 'keyword'> & {
  createdAtRange?: DateRangeFormValue
}

type CustomerTagRow = CustomerTagEditorRecord & {
  creator: AccountDisplay
  createdAt: Date
  updatedAt: Date
}

const customerTagSearchSchema = z.object({
  current: z.coerce.number().int().min(1).catch(DEFAULT_QUERY.current),
  pageSize: z.coerce.number().int().min(1).max(100).catch(DEFAULT_QUERY.pageSize),
  keyword: z.string().trim().max(100).optional().catch(undefined),
  ...creatorMemberSearchShape,
  ...createdAtSearchShape,
  ...createdAtSortSearchShape
})

const listQuery = createListQueryPersistence(
  'super-mega:customer-management:customer-tags:list-query',
  customerTagSearchSchema
)

export const Route = createFileRoute('/_authenticated/customer-management/customer-tags')({
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  component: CustomerTagsPage,
  head: () => ({ meta: [{ title: '客户标签 · Super Mega' }] }),
  validateSearch: listQuery.validateSearch
})

function CustomerTagsPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message, modal } = App.useApp()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search: query, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [editing, setEditing] = useState<CustomerTagRow | 'new'>()
  const [selectedCustomerTagIds, setSelectedCustomerTagIds] = useState<number[]>([])
  const canManage =
    currentSession.isSuperAdmin ||
    currentSession.permissions.some((permission) => permission.startsWith('customer.') && !permission.endsWith('.read'))
  const tags = useQuery({
    ...trpc.customer.tagList.queryOptions(query),
    placeholderData: keepPreviousData
  })
  const memberCreator = useMemberCreatorOptions('customer.tag.read', query.creatorMemberId)
  const remove = useMutation(trpc.customer.removeTag.mutationOptions())
  const batchRemove = useMutation(trpc.customer.batchRemoveTag.mutationOptions())

  const closeEditor = () => setEditing(undefined)
  const handleQueryChange = () => {
    closeEditor()
    setSelectedCustomerTagIds([])
  }
  const invalidateTags = () =>
    Promise.all([
      queryClient.invalidateQueries(trpc.customer.list.queryFilter()),
      queryClient.invalidateQueries(trpc.customer.tagList.queryFilter()),
      queryClient.invalidateQueries(trpc.customer.tagOptions.queryFilter())
    ])
  const table = usePageTableNavigation<CustomerTagQuery, CustomerTagSearchFormValues>({
    defaultSearch: DEFAULT_QUERY,
    getFormInitialValues: (search) => ({
      createdAtRange: getDateRangeFormValue(search.createdFrom, search.createdTo),
      creatorMemberId: search.creatorMemberId,
      keyword: search.keyword
    }),
    getSearchFromForm: (values) => ({
      ...getCreatedAtSearch(values.createdAtRange),
      creatorMemberId: values.creatorMemberId,
      keyword: values.keyword?.trim() || undefined
    }),
    navigate: navigateSearch,
    onQueryChange: handleQueryChange,
    search: query,
    showTotal: (total) => `共 ${total} 个客户标签`
  })

  const handleTableChange: NonNullable<TableProps<CustomerTagRow>['onChange']> =
    /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
    (_pagination, _filters, sorter, extra) => {
      if (extra.action !== 'sort') {
        return
      }
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter
      const sortOrder = getCreatedAtSortOrder(currentSorter.order)
      handleQueryChange()
      void navigate({
        replace: true,
        search: (previous) => ({
          ...previous,
          current: 1,
          sortField: 'createdAt',
          sortOrder
        })
      })
    }
  /* eslint-enable max-params */

  const removeCustomerTag = async (tag: CustomerTagRow) => {
    try {
      await remove.mutateAsync({ id: tag.id })
      setSelectedCustomerTagIds([])
      void message.success('客户标签已删除')
      await queryClient.invalidateQueries(trpc.customer.list.queryFilter())
      await queryClient.invalidateQueries(trpc.customer.tagOptions.queryFilter())
      if (tags.data?.data.length === 1 && query.current > 1) {
        await navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) })
      } else {
        await queryClient.invalidateQueries(trpc.customer.tagList.queryFilter())
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '客户标签删除失败'))
    }
  }

  const confirmRemoveCustomerTag = (tag: CustomerTagRow) => {
    modal.confirm({
      content: '删除后不会再出现在客户标签选择器中，已有客户的历史标签保留。',
      okButtonProps: { danger: true },
      onOk: () => removeCustomerTag(tag),
      title: '确认删除该客户标签？'
    })
  }

  const removeSelectedCustomerTags = async () => {
    try {
      const result = await batchRemove.mutateAsync({ ids: selectedCustomerTagIds })
      const allVisibleRowsSelected =
        selectedCustomerTagIds.length === (tags.data?.data.length ?? 0) && selectedCustomerTagIds.length > 0
      setSelectedCustomerTagIds([])
      void message.success(`已删除 ${result.count} 个客户标签`)
      await queryClient.invalidateQueries(trpc.customer.list.queryFilter())
      await queryClient.invalidateQueries(trpc.customer.tagOptions.queryFilter())
      if (allVisibleRowsSelected && query.current > 1) {
        await navigateSearch({ search: (previous) => ({ ...previous, current: previous.current - 1 }) })
      } else {
        await queryClient.invalidateQueries(trpc.customer.tagList.queryFilter())
      }
    } catch (error) {
      void message.error(getErrorMessage(error, '批量删除客户标签失败'))
    }
  }

  const confirmRemoveSelectedCustomerTags = () => {
    modal.confirm({
      content: '删除后不会再出现在客户标签选择器中，已有客户的历史标签保留。',
      okButtonProps: { danger: true },
      okText: '确认删除',
      onOk: removeSelectedCustomerTags,
      title: `删除已选 ${selectedCustomerTagIds.length} 个客户标签？`
    })
  }

  const batchMenu: MenuProps = {
    items: [{ danger: true, key: 'delete', label: '批量删除' }],
    onClick: ({ key }) => {
      if (key === 'delete') {
        confirmRemoveSelectedCustomerTags()
      }
    }
  }

  const columns: ProColumns<CustomerTagRow>[] = [
    {
      dataIndex: 'keyword',
      fieldProps: { allowClear: true, placeholder: '标签名称或 ID' },
      hideInTable: true,
      title: '关键词 / ID'
    },
    {
      dataIndex: 'creatorMemberId',
      fieldProps: {
        allowClear: true,
        loading: memberCreator.isLoading,
        options: memberCreator.options,
        placeholder: '选择创建人',
        ...proRemoteSearch(memberCreator.onSearch)
      },
      hideInTable: true,
      title: '创建人',
      valueType: 'select'
    },
    {
      dataIndex: 'createdAtRange',
      hideInTable: true,
      title: '创建时间',
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      dataIndex: 'id',
      copyable: true,
      renderText: (value?: number) => (value === undefined ? '—' : String(value)),
      search: false,
      title: 'ID',
      width: '7rem'
    },
    {
      dataIndex: 'name',
      render: (_, tag) => (
        <Tag className="max-w-full truncate" color={tag.color} title={tag.name}>
          {tag.name}
        </Tag>
      ),
      search: false,
      title: '标签名称',
      width: '16rem'
    },
    {
      dataIndex: 'color',
      render: (_, tag) => <Tag color={tag.color}>{tag.color}</Tag>,
      search: false,
      title: '标签颜色',
      width: '12rem'
    },
    {
      dataIndex: 'creator',
      render: (_, tag) => <UserAvatarName avatar={tag.creator.avatar} name={tag.creator.name} size={28} />,
      search: false,
      title: '创建人',
      width: '10rem'
    },
    {
      dataIndex: 'createdAt',
      search: false,
      sortOrder: query.sortOrder === 'asc' ? 'ascend' : 'descend',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      title: '创建时间',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      dataIndex: 'updatedAt',
      search: false,
      title: '最近更新',
      valueType: 'dateTime',
      width: '12rem'
    },
    {
      align: 'center',
      fixed: 'right',
      render: (_, tag) =>
        canManage ? (
          <TableRowActions
            resource="customer.tag"
            actions={[
              { key: 'edit', label: '编辑', onClick: () => setEditing(tag) },
              { danger: true, key: 'delete', label: '删除', onClick: () => confirmRemoveCustomerTag(tag) }
            ]}
          />
        ) : (
          '—'
        ),
      search: false,
      title: '操作',
      valueType: 'option',
      width: '8rem'
    }
  ]

  return (
    <PageContainer
      breadcrumbRender={false}
      content="维护客户标签名称和颜色，用于客户档案中快速识别和筛选。"
      title="客户标签"
    >
      {tags.isError ? (
        <Alert
          action={
            <Button onClick={() => void tags.refetch()} size="small">
              重试
            </Button>
          }
          className="mb-4"
          showIcon
          title={getErrorMessage(tags.error, '客户标签列表加载失败')}
          type="error"
        />
      ) : null}
      <ProTable<CustomerTagRow, CustomerTagSearchFormValues>
        columns={columns}
        columnsState={{ persistenceKey: 'customer-management-customer-tags:table', persistenceType: 'localStorage' }}
        dataSource={tags.data?.data ?? []}
        form={{ initialValues: table.formInitialValues }}
        formRef={table.formRef}
        loading={{ delay: 150, spinning: tags.isFetching }}
        onChange={handleTableChange}
        onReset={table.onReset}
        onSubmit={table.onSubmit}
        options={{ reload: () => void tags.refetch() }}
        pagination={{ ...table.pagination, total: tags.data?.total ?? 0 }}
        rowKey="id"
        rowSelection={
          canManage
            ? {
                onChange: (keys) => setSelectedCustomerTagIds(keys.map(Number)),
                selectedRowKeys: selectedCustomerTagIds
              }
            : undefined
        }
        scroll={{ x: '78rem' }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        size="small"
        toolBarRender={() =>
          canManage
            ? [
                <AuthorizedDropdown
                  resource="customer.tag"
                  key="batch-actions"
                  menu={batchMenu}
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button disabled={selectedCustomerTagIds.length === 0} icon={<ListChecks aria-hidden size={16} />}>
                    批量操作 <ChevronDown aria-hidden size={14} />
                  </Button>
                </AuthorizedDropdown>,
                <Authorized key="new" permissions={['customer.tag.create']}>
                  <Button
                    icon={<Plus aria-hidden size={16} />}
                    key="new"
                    onClick={() => setEditing('new')}
                    type="primary"
                  >
                    新建标签
                  </Button>
                </Authorized>
              ]
            : []
        }
      />

      <CustomerTagEditorModal
        editing={editing}
        onClose={closeEditor}
        onSaved={async () => {
          closeEditor()
          await invalidateTags()
        }}
      />
    </PageContainer>
  )
}
